/**
 * Named views kept in one account-config key (I-3024). Lifted from the ERP
 * tasks table: the payload is a type parameter, and the caller supplies the
 * compare key. Tasks keeps filters and sort; the sales-document search keeps
 * query, scope and filters. Saving and removing one view is a merge patch of
 * the map by id, so the other members stay.
 */
import { computed, ref, watch, type ComputedRef, type Ref } from "vue";
import type { AccountConfigHandle } from "./accountConfig";
import { cloneJson } from "./mergePatch";
import { SAVED_VIEWS_FIELD, savedViewsPatch } from "./savedViews";

export interface SavedViewCodec<TView extends { id: string; name: string }, TState> {
  /** Views in display order, from the stored document. */
  viewsOf(document: unknown): TView[];
  /** The part of a stored view the compare key reads. */
  stateOf(view: TView): TState;
  /** The map member for one view. `order` is its place behind the others. */
  entryOf(view: TView, order: number): Record<string, unknown>;
  /** The place a new view takes. */
  nextOrder(document: unknown): number;
  /** A view from the live state. `state` is a clone; mutating it is safe. */
  create(id: string, name: string, state: TState): TView;
}

export interface UseSavedViewsOptions<
  TView extends { id: string; name: string },
  TState,
  TDocument = Record<string, unknown>,
> {
  /** No handle (no key): nothing is stored, `save` returns null. */
  config: Ref<AccountConfigHandle<TDocument> | null>;
  current: Ref<TState>;
  /** Same key for the live state and for `codec.stateOf`. */
  signature: (state: TState) => string;
  /**
   * When this returns false the live state matches no view (an empty tasks
   * table). Defaults to always matchable.
   */
  matchable?: (state: TState) => boolean;
  codec: SavedViewCodec<TView, TState>;
  /** Map field on the document. Default `views`. */
  field?: string;
}

export interface SavedViews<TView extends { id: string; name: string }> {
  views: ComputedRef<TView[]>;
  activeViewId: Ref<string | null>;
  /** Stores the live state under `name`. Null when there is no key or no name. */
  save(name: string): TView | null;
  remove(id: string): void;
  /** The stored view, cloned, or null when the id is gone. */
  apply(id: string): TView | null;
}

function createId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function useSavedViews<
  TView extends { id: string; name: string },
  TState,
  TDocument = Record<string, unknown>,
>(options: UseSavedViewsOptions<TView, TState, TDocument>): SavedViews<TView> {
  const field = options.field ?? SAVED_VIEWS_FIELD;
  const activeViewId = ref<string | null>(null);
  const views = computed(() => options.codec.viewsOf(options.config.value?.effective.value));

  function patch(partial: Record<string, unknown>): void {
    const handle = options.config.value;
    if (!handle) return;
    void handle.patch(partial as Partial<TDocument>);
  }

  function save(name: string): TView | null {
    const handle = options.config.value;
    if (!handle) return null;
    const cleanName = name.trim();
    if (!cleanName) return null;
    const view = options.codec.create(createId(), cleanName, cloneJson(options.current.value));
    const order = options.codec.nextOrder(handle.effective.value);
    patch(savedViewsPatch(view.id, options.codec.entryOf(view, order), field));
    activeViewId.value = view.id;
    return view;
  }

  function remove(id: string): void {
    patch(savedViewsPatch(id, null, field));
    if (activeViewId.value === id) activeViewId.value = null;
  }

  function apply(id: string): TView | null {
    const view = views.value.find((candidate) => candidate.id === id) ?? null;
    activeViewId.value = view?.id ?? null;
    return view ? cloneJson(view) : null;
  }

  watch(
    () => options.config.value?.key,
    () => {
      activeViewId.value = null;
    },
  );

  watch(
    [
      () => options.signature(options.current.value),
      () =>
        views.value.map((view) => ({
          id: view.id,
          signature: options.signature(options.codec.stateOf(view)),
        })),
    ],
    () => {
      if (options.matchable && !options.matchable(options.current.value)) {
        activeViewId.value = null;
        return;
      }
      const current = options.signature(options.current.value);
      activeViewId.value =
        views.value.find((view) => options.signature(options.codec.stateOf(view)) === current)
          ?.id ?? null;
    },
    { immediate: true },
  );

  return { views, activeViewId, save, remove, apply };
}
